import {
	UntypedFormGroup,
	UntypedFormControl,
	Validators,
} from '@angular/forms';
import { BaseResponse } from './base-response.model';
import { Validation } from './validation.model';
import { DateTimeUtility } from '../utility/date-time.utility';

export interface InsuranceResponse extends BaseResponse {
	message: string;
	insurance: InsuranceJSON;
}

export interface InsuranceJSON {
	insuranceType: string;
	groupId: string;
	subscriberId: string;
	personNumber: string;
	empNumber: string;
	relationship: string;
	planCode: string;
	coverageRider: string;
	effectiveDate: string;
	originalEffectiveDate: string;
	terminationDate: string;
	citizenship: string;
	workLocation: string;
	deptCode: string;
	reportingCode: string;
	center: string;
	department: string;
	empName: string;
	dateOfBirth: string;
	nationality: string;
	employeeType: string;
	saicoMemberNumber: string;
	saicoEmployeeNumber: string;
	saicoCardExpiryDate: string;
}

export class Insurance {
	constructor(
		public insuranceType: string,
		public groupId: string,
		public subscriberId: string,
		public personNumber: string,
		public empNumber: string,
		public relationship: string,
		public planCode: string,
		public coverageRider: string,
		public effectiveDate: Date,
		public originalEffectiveDate: Date,
		public terminationDate: Date,
		public citizenship: string,
		public workLocation: string,
		public deptCode: string,
		public reportingCode: string,
		public center: string,
		public department: string,
		public empName: string,
		public dateOfBirth: Date,
		public nationality: string,
		public employeeType: string,
		public saicoMemberNumber: string,
		public saicoEmployeeNumber: string,
		public saicoCardExpiryDate: Date
	) {}

	static fromJSON(json: InsuranceJSON): Insurance {
		return Object.assign(Object.create(Insurance.prototype), json, {
			effectiveDate: json.effectiveDate
				? DateTimeUtility.fromDatabaseDate(json.effectiveDate)
				: null,
			originalEffectiveDate: json.originalEffectiveDate
				? DateTimeUtility.fromDatabaseDate(json.originalEffectiveDate)
				: null,
			terminationDate: json.terminationDate
				? DateTimeUtility.fromDatabaseDate(json.terminationDate)
				: null,
			dateOfBirth: json.dateOfBirth
				? DateTimeUtility.fromDatabaseDate(json.dateOfBirth)
				: null,
			saicoCardExpiryDate: json.saicoCardExpiryDate
				? DateTimeUtility.fromDatabaseDate(json.saicoCardExpiryDate)
				: null,
		});
	}

	static createFormGroup(
		insurance: Insurance,
		validation: Validation
	): UntypedFormGroup {
		return new UntypedFormGroup({
			insuranceType: new UntypedFormControl(
				insurance ? insurance.insuranceType : null
			),
			groupId: new UntypedFormControl(
				insurance ? insurance.groupId : '',
				[
					Validators.maxLength(validation.comment.maxLength),
					Validators.pattern(validation.comment.pattern),
				]
			),
			subscriberId: new UntypedFormControl(
				insurance ? insurance.subscriberId : '',
				[
					Validators.maxLength(validation.comment.maxLength),
					Validators.pattern(validation.comment.pattern),
				]
			),
			personNumber: new UntypedFormControl(
				insurance ? insurance.personNumber : '',
				[
					Validators.maxLength(validation.comment.maxLength),
					Validators.pattern(validation.comment.pattern),
				]
			),
			empNumber: new UntypedFormControl(
				insurance ? insurance.empNumber : '',
				[
					Validators.maxLength(validation.comment.maxLength),
					Validators.pattern(validation.comment.pattern),
				]
			),
			relationship: new UntypedFormControl(
				insurance ? insurance.relationship : '',
				[
					Validators.maxLength(validation.comment.maxLength),
					Validators.pattern(validation.comment.pattern),
				]
			),
			planCode: new UntypedFormControl(
				insurance ? insurance.planCode : '',
				[
					Validators.maxLength(validation.comment.maxLength),
					Validators.pattern(validation.comment.pattern),
				]
			),
			coverageRider: new UntypedFormControl(
				insurance ? insurance.coverageRider : '',
				[
					Validators.maxLength(validation.comment.maxLength),
					Validators.pattern(validation.comment.pattern),
				]
			),
			effectiveDate: new UntypedFormControl(
				insurance ? insurance.effectiveDate : null
			),
			originalEffectiveDate: new UntypedFormControl(
				insurance ? insurance.originalEffectiveDate : null
			),
			terminationDate: new UntypedFormControl(
				insurance ? insurance.terminationDate : null
			),
			citizenship: new UntypedFormControl(
				insurance ? insurance.citizenship : '',
				[
					Validators.maxLength(validation.comment.maxLength),
					Validators.pattern(validation.comment.pattern),
				]
			),
			workLocation: new UntypedFormControl(
				insurance ? insurance.workLocation : '',
				[
					Validators.maxLength(validation.comment.maxLength),
					Validators.pattern(validation.comment.pattern),
				]
			),
			deptCode: new UntypedFormControl(
				insurance ? insurance.deptCode : '',
				[
					Validators.maxLength(validation.comment.maxLength),
					Validators.pattern(validation.comment.pattern),
				]
			),
			reportingCode: new UntypedFormControl(
				insurance ? insurance.reportingCode : '',
				[
					Validators.maxLength(validation.comment.maxLength),
					Validators.pattern(validation.comment.pattern),
				]
			),
			center: new UntypedFormControl(insurance ? insurance.center : '', [
				Validators.maxLength(validation.comment.maxLength),
				Validators.pattern(validation.comment.pattern),
			]),
			department: new UntypedFormControl(
				insurance ? insurance.department : '',
				[
					Validators.maxLength(validation.comment.maxLength),
					Validators.pattern(validation.comment.pattern),
				]
			),
			empName: new UntypedFormControl(
				insurance ? insurance.empName : '',
				[
					Validators.maxLength(validation.comment.maxLength),
					Validators.pattern(validation.comment.pattern),
				]
			),
			dateOfBirth: new UntypedFormControl(
				insurance ? insurance.dateOfBirth : null
			),
			nationality: new UntypedFormControl(
				insurance ? insurance.nationality : '',
				[
					Validators.maxLength(validation.comment.maxLength),
					Validators.pattern(validation.comment.pattern),
				]
			),
			employeeType: new UntypedFormControl(
				insurance ? insurance.employeeType : '',
				[
					Validators.maxLength(validation.comment.maxLength),
					Validators.pattern(validation.comment.pattern),
				]
			),
			saicoMemberNumber: new UntypedFormControl(
				insurance ? insurance.saicoMemberNumber : '',
				[
					Validators.maxLength(validation.comment.maxLength),
					Validators.pattern(validation.comment.pattern),
				]
			),
			saicoEmployeeNumber: new UntypedFormControl(
				insurance ? insurance.saicoEmployeeNumber : '',
				[
					Validators.maxLength(validation.comment.maxLength),
					Validators.pattern(validation.comment.pattern),
				]
			),
			saicoCardExpiryDate: new UntypedFormControl(
				insurance ? insurance.saicoCardExpiryDate : null
			),
		});
	}

	static fromFormGroup(fg: UntypedFormGroup): Insurance {
		return Object.assign(Object.create(Insurance.prototype), {
			insuranceType: fg.get('insuranceType').value,
			groupId: fg.get('groupId').value,
			subscriberId: fg.get('subscriberId').value,
			personNumber: fg.get('personNumber').value,
			empNumber: fg.get('empNumber').value,
			relationship: fg.get('relationship').value,
			planCode: fg.get('planCode').value,
			coverageRider: fg.get('coverageRider').value,
			effectiveDate: DateTimeUtility.toDatabaseDateString(
				fg.get('effectiveDate').value
			),
			originalEffectiveDate: DateTimeUtility.toDatabaseDateString(
				fg.get('originalEffectiveDate').value
			),
			terminationDate: DateTimeUtility.toDatabaseDateString(
				fg.get('terminationDate').value
			),
			citizenship: fg.get('citizenship').value,
			workLocation: fg.get('workLocation').value,
			deptCode: fg.get('deptCode').value,
			reportingCode: fg.get('reportingCode').value,
			center: fg.get('center').value,
			department: fg.get('department').value,
			empName: fg.get('empName').value,
			dateOfBirth: DateTimeUtility.toDatabaseDateString(
				fg.get('dateOfBirth').value
			),
			nationality: fg.get('nationality').value,
			employeeType: fg.get('employeeType').value,
			saicoMemberNumber: fg.get('saicoMemberNumber').value,
			saicoEmployeeNumber: fg.get('saicoEmployeeNumber').value,
			saicoCardExpiryDate: DateTimeUtility.toDatabaseDateString(
				fg.get('saicoCardExpiryDate').value
			),
		});
	}
}
